import { Container, Sprite, Text, type Texture } from "pixi.js";
import type { PresentationTaskRunner, TweenEasing } from "@slot/presentation";
import { FittedLine, TEXT } from "../../views/textStyles";
import type { SavedTotalPulseTuning } from "../../tuning/savedTotalTuning";
import type { VsWildLabelTarget } from "../vs-battle/VsWildView";

/**
 * `HudLayer/PersistentMultiplierPanel` from the scene dump, y-down in composition-root units: the panel centred just
 * above the grid frame's top edge, its plate (`backgrounds/feature_mult_bg`, stretched past the panel by its widget's
 * margins) and the value label filling the panel (Roboto Medium set bold, 80, shrunk to fit).
 */
const SCENE = {
  y: -538.908,
  box: { width: 240.289, height: 120.852 },
  plate: { width: 302.676, height: 207.862 },
  fontSize: 80,
} as const;

/** One fly of the value: where it lands, and its clock, scope, time, bow and easing. */
export interface SavedTotalFlight {
  readonly target: VsWildLabelTarget;
  readonly runner: PresentationTaskRunner;
  /** The battle's scope: the fly is slammed with the battle, and plays at its pace. */
  readonly scope: string;
  readonly seconds: number;
  /** How far the path bows above its higher end, in the fly layer's units (design units). */
  readonly arc: number;
  readonly easing: TweenEasing;
}

/**
 * The Super Bonus saved total over the grid (Cocos `PersistentMultiplierPanel` and `PersistentMultiplierFly`): hidden
 * until the presenter shows it, its value as given ("9x"), a pulse of the plate and value together, and the value's fly
 * into a battle's top label. The fly is a copy of the value (the panel keeps showing it) in the fly layer, above the
 * grid and this panel: a quadratic arc from the value to the label's spot, bowed above the higher end, its size going
 * from the value's to the label's.
 */
export class SavedTotalView extends Container {
  private readonly root: Container;
  private readonly value: FittedLine;
  private readonly flyLayer: Container;
  /** Bumped by each pulse and by `hide`: an older pulse stops writing. */
  private generation = 0;

  public constructor(input: { plate: Texture | null; flyLayer: Container }) {
    super({ label: "SavedTotal" });
    this.flyLayer = input.flyLayer;
    this.y = SCENE.y;
    this.root = new Container({ label: "Root" });
    if (input.plate) {
      const plate = new Sprite({ texture: input.plate, anchor: 0.5, label: "Plate" });
      plate.width = SCENE.plate.width;
      plate.height = SCENE.plate.height;
      this.root.addChild(plate);
    }
    this.value = new FittedLine({ style: TEXT.multiplier, fontSize: SCENE.fontSize, y: 0, box: SCENE.box, label: "SavedTotalValue" });
    this.root.addChild(this.value);
    this.addChild(this.root);
    this.visible = false;
  }

  public get shown(): boolean {
    return this.visible;
  }

  public show(): void {
    this.visible = true;
  }

  /** Hidden, a running pulse stopped at the rest scale. */
  public hide(): void {
    this.generation += 1;
    this.root.scale.set(1);
    this.visible = false;
  }

  /** The value as shown ("9x"). */
  public setValue(text: string): void {
    this.value.setLine(text);
  }

  /** The plate and the value swell and settle, unscoped (a slam does not cut it short). */
  public async pulse(tuning: SavedTotalPulseTuning, runner: PresentationTaskRunner): Promise<void> {
    const generation = ++this.generation;
    const scaleTo = (scale: number): void => {
      if (this.generation === generation) this.root.scale.set(scale);
    };
    await runner.tweenProgress(tuning.upSeconds, (t) => scaleTo(1 + (tuning.scale - 1) * t), undefined, { easing: tuning.upEasing });
    if (this.generation !== generation) return;
    await runner.tweenProgress(tuning.downSeconds, (t) => scaleTo(tuning.scale + (1 - tuning.scale) * t), undefined, { easing: tuning.downEasing });
    scaleTo(1);
  }

  /** A copy of the value flies to `flight.target` and is gone when it lands (a slam lands it at once). */
  public async flyValueTo(flight: SavedTotalFlight): Promise<void> {
    const layer = this.flyLayer;
    const layerScale = globalScale(layer);
    const from = layer.toLocal(this.value.getGlobalPosition());
    const to = layer.toLocal({ x: flight.target.x, y: flight.target.y });
    const fromScale = globalScale(this.value) / layerScale;
    const toScale = flight.target.scale / layerScale;
    const control = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - flight.arc };
    const copy = new Text({ text: this.value.text, style: { ...TEXT.multiplier, fontSize: SCENE.fontSize }, anchor: 0.5, label: "SavedTotalFly" });
    copy.position.copyFrom(from);
    copy.scale.set(fromScale);
    layer.addChild(copy);
    await flight.runner.tweenProgress(
      flight.seconds,
      (t) => {
        const u = 1 - t;
        copy.position.set(u * u * from.x + 2 * u * t * control.x + t * t * to.x, u * u * from.y + 2 * u * t * control.y + t * t * to.y);
        copy.scale.set(fromScale + (toScale - fromScale) * t);
      },
      flight.scope,
      { easing: flight.easing },
    );
    copy.destroy();
  }
}

/** Global units per local unit of `node` (its own scale included). */
function globalScale(node: Container): number {
  const origin = node.toGlobal({ x: 0, y: 0 });
  const unit = node.toGlobal({ x: 1, y: 0 });
  return Math.hypot(unit.x - origin.x, unit.y - origin.y);
}
